import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../utils/logger'
import { 
  ArrowLeft, 
  Plus, 
  Save, 
  Trash2, 
  Bot, 
  MessageSquare, 
  Settings,
  ChevronDown,
  ChevronRight,
  Edit,
  Tag
} from 'lucide-react'
import { menuService, Menu, CreateMenuData } from '../services/menuService'
import { tagService, type ContactTag } from '../services/tagService'
import { botService } from '../services/botService'
import { templateService } from '../services/templateService'
import { ResponseTemplate } from '../types/template.types'
import { Loading } from '../components/common/Loading'
import toast from 'react-hot-toast'

interface Bot {
  id: number
  name: string
  session_name?: string
  status: string
  isConnected: boolean
  phone_number?: string
  created_at: string
}

const MenuCreate: React.FC = () => {
  const navigate = useNavigate()
  const [bots, setBots] = useState<Bot[]>([])
  const [menus, setMenus] = useState<Menu[]>([])
  const [templates, setTemplates] = useState<ResponseTemplate[]>([])
  const [selectedBot, setSelectedBot] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set())
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [menuToDelete, setMenuToDelete] = useState<Menu | null>(null)
  const [showAllBots, setShowAllBots] = useState(false)
  const [contactTags, setContactTags] = useState<ContactTag[]>([])
  const [applyAutoConversationTag, setApplyAutoConversationTag] = useState(false)

  // Formulário de criação de menu
  const [formData, setFormData] = useState<CreateMenuData>({
    bot_session_id: 0,
    title: '',
    content: '',
    type: 'static',
    requires_human: false,
    parent_id: undefined,
    display_order: 0,
    use_template: false,
    response_template_id: undefined,
    auto_conversation_tag_id: null
  })

  useEffect(() => {
    loadBots()
    loadTemplates()
    loadContactTags()
  }, [])

  const loadContactTags = async () => {
    try {
      const res = await tagService.listTags()
      if (res.success && res.data) {
        setContactTags(res.data)
      }
    } catch (error) {
      logger.error('Erro ao carregar tags:', error)
    }
  }

  useEffect(() => {
    if (selectedBot) {
      loadMenus()
    }
  }, [selectedBot])

  const loadBots = async () => {
    try {
      const response = await botService.getBots()
      if (response.success && response.data) {
        // Corrigir isConnected baseado no status do banco
        const botsFixed = (response.data.bots || []).map((bot: any) => ({
          ...bot,
          isConnected: bot.status === 'connected'
        }))
        setBots(botsFixed)
        if (botsFixed && botsFixed.length > 0) {
          setSelectedBot(botsFixed[0].id)
          setFormData(prev => ({ ...prev, bot_session_id: botsFixed[0].id }))
        }
      }
    } catch (error) {
      logger.error('Erro ao carregar bots:', error)
      toast.error('Erro ao carregar bots')
    } finally {
      setIsLoading(false)
    }
  }

  const loadTemplates = async () => {
    try {
      const response = await templateService.getTemplates(1, 100)
      if (response.success) {
        setTemplates(response.data.templates)
      }
    } catch (error) {
      logger.error('Erro ao carregar templates:', error)
      toast.error('Erro ao carregar templates')
    }
  }

  const loadMenus = async () => {
    if (!selectedBot && !showAllBots) return

    try {
      // Se showAllBots estiver ativo, carregar todos os menus (sem filtro de bot)
      const botId = showAllBots ? undefined : (selectedBot || undefined)
      const response = await menuService.getMenus(botId, 1, 100)
      if (response.success) {
        // Organizar menus em hierarquia
        const menuMap = new Map<number, Menu>()
        const rootMenus: Menu[] = []

        response.data.menus.forEach((menu: Menu) => {
          menu.children = []
          menuMap.set(menu.id, menu)
        })

        response.data.menus.forEach((menu: Menu) => {
          if (menu.parent_id) {
            const parent = menuMap.get(menu.parent_id)
            if (parent) {
              parent.children = parent.children || []
              parent.children.push(menu)
            }
          } else {
            rootMenus.push(menu)
          }
        })

        setMenus(rootMenus)
      }
    } catch (error) {
      logger.error('Erro ao carregar menus:', error)
      toast.error('Erro ao carregar menus')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }

    if (!formData.bot_session_id) {
      toast.error('Selecione um bot')
      return
    }

    if (applyAutoConversationTag && (formData.auto_conversation_tag_id == null || formData.auto_conversation_tag_id <= 0)) {
      toast.error('Selecione a tag a aplicar na conversa ou desmarque a opção')
      return
    }

    if (isEditing && editingMenu) {
      await handleUpdateMenu()
    } else {
      await handleCreateMenu()
    }
  }

  const handleCreateMenu = async () => {
    try {
      setIsCreating(true)
      const payload: CreateMenuData = {
        ...formData,
        auto_conversation_tag_id: applyAutoConversationTag ? formData.auto_conversation_tag_id ?? null : null
      }
      const response = await menuService.createMenu(payload)
      
      if (response.success) {
        toast.success('Menu criado com sucesso!')
        resetForm()
        loadMenus()
      } else {
        toast.error(response.message || 'Erro ao criar menu')
      }
    } catch (error) {
      logger.error('Erro ao criar menu:', error)
      toast.error('Erro ao criar menu')
    } finally {
      setIsCreating(false)
    }
  }

  const handleUpdateMenu = async () => {
    if (!editingMenu) return

    try {
      setIsCreating(true)
      const payload: CreateMenuData = {
        ...formData,
        auto_conversation_tag_id: applyAutoConversationTag ? formData.auto_conversation_tag_id ?? null : null
      }
      const response = await menuService.updateMenu(editingMenu.id, payload)
      
      if (response.success) {
        toast.success('Menu atualizado com sucesso!')
        resetForm()
        loadMenus()
      } else {
        toast.error(response.message || 'Erro ao atualizar menu')
      }
    } catch (error) {
      logger.error('Erro ao atualizar menu:', error)
      toast.error('Erro ao atualizar menu')
    } finally {
      setIsCreating(false)
    }
  }

  const handleEditMenu = (menu: Menu) => {
    setEditingMenu(menu)
    setIsEditing(true)
    const tagId = menu.auto_conversation_tag_id ?? null
    setApplyAutoConversationTag(tagId != null && tagId > 0)
    setFormData({
      bot_session_id: menu.bot_session_id,
      title: menu.title,
      content: menu.content || '',
      type: menu.type,
      requires_human: menu.requires_human,
      parent_id: menu.parent_id,
      display_order: menu.display_order,
      use_template: menu.use_template,
      response_template_id: menu.response_template_id,
      auto_conversation_tag_id: tagId
    })
  }

  const handleDeleteMenu = async (menu: Menu) => {
    setMenuToDelete(menu)
    setShowDeleteModal(true)
  }

  const confirmDeleteMenu = async () => {
    if (!menuToDelete) return

    try {
      const result = await menuService.deleteMenu(menuToDelete.id)
      if (result.success) {
        toast.success('Menu deletado com sucesso!')
        loadMenus()
      } else {
        toast.error(result.message || 'Erro ao deletar menu')
      }
    } catch (error) {
      logger.error('Erro ao deletar menu:', error)
      toast.error('Erro ao deletar menu')
    } finally {
      setShowDeleteModal(false)
      setMenuToDelete(null)
    }
  }

  const resetForm = () => {
    setFormData({
      bot_session_id: selectedBot || 0,
      title: '',
      content: '',
      type: 'static',
      requires_human: false,
      parent_id: undefined,
      display_order: 0,
      use_template: false,
      response_template_id: undefined,
      auto_conversation_tag_id: null
    })
    setApplyAutoConversationTag(false)
    setEditingMenu(null)
    setIsEditing(false)
  }

  const toggleMenuExpansion = (menuId: number) => {
    const newExpanded = new Set(expandedMenus)
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId)
    } else {
      newExpanded.add(menuId)
    }
    setExpandedMenus(newExpanded)
  }

  const renderMenuTree = (menus: Menu[], level = 0) => {
    return menus.map(menu => {
      const associatedBot = Array.isArray(bots) ? bots.find(b => b.id === menu.bot_session_id) : null
      
      return (
        <div key={menu.id} className="border rounded-lg mb-2">
          <div 
            className={`flex items-center justify-between p-4 bg-gray-50 ${
              level > 0 ? 'ml-4' : ''
            }`}
          >
            <div className="flex items-center space-x-3">
              {menu.children && menu.children.length > 0 && (
                <button
                  onClick={() => toggleMenuExpansion(menu.id)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {expandedMenus.has(menu.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              )}
              <div className="flex items-center space-x-3">
                {menu.type === 'static' ? (
                  <MessageSquare className="w-4 h-4 text-blue-500" />
                ) : (
                  <Settings className="w-4 h-4 text-green-500" />
                )}
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{menu.title}</span>
                    {menu.requires_human && (
                      <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                        Humano
                      </span>
                    )}
                    {(menu.auto_conversation_tag_id != null && menu.auto_conversation_tag_id > 0) || menu.autoConversationTag ? (
                      <span
                        className="text-xs px-2 py-1 rounded flex items-center gap-1 max-w-[200px]"
                        style={{
                          backgroundColor: `${menu.autoConversationTag?.color || '#e0e7ff'}33`,
                          color: menu.autoConversationTag?.color || '#4338ca'
                        }}
                        title="Tag aplicada à conversa ao escolher esta opção"
                      >
                        <Tag className="w-3 h-3 shrink-0" />
                        <span className="truncate">
                          {menu.autoConversationTag?.name ?? `Tag #${menu.auto_conversation_tag_id}`}
                        </span>
                      </span>
                    ) : null}
                    {!menu.is_active && (
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        Inativo
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-gray-500">
                      Bot: {associatedBot ? (associatedBot.session_name || associatedBot.name) : 'Bot não encontrado'}
                    </span>
                    {associatedBot && (
                      <div className={`w-2 h-2 rounded-full ${
                        associatedBot.isConnected ? 'bg-green-500' : 'bg-red-500'
                      }`}></div>
                    )}
                    <span className="text-xs text-gray-500">
                      Ordem: {menu.display_order}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleEditMenu(menu)}
                className="p-2 text-blue-500 hover:bg-blue-100 rounded"
                title="Editar menu"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteMenu(menu)}
                className="p-2 text-red-500 hover:bg-red-100 rounded"
                title="Deletar menu"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {menu.children && menu.children.length > 0 && expandedMenus.has(menu.id) && (
            <div className="ml-4">
              {renderMenuTree(menu.children, level + 1)}
            </div>
          )}
        </div>
      )
    })
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isEditing ? 'Editar Menu' : 'Criar Menus'}
                </h1>
                <p className="text-gray-600">Gerencie os menus e submenus dos seus bots</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulário de Criação */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Plus className="w-5 h-5 mr-2 text-blue-500" />
                Novo Menu
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Seleção do Bot */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bot Associado <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Selecione qual bot este menu será associado. Cada menu pertence a um bot específico.
                  {!selectedBot && (
                    <span className="block mt-2 text-amber-600 font-medium">
                      ⚠️ Selecione um bot para continuar
                    </span>
                  )}
                </p>

                {/* Select Dropdown Compacto */}
                {Array.isArray(bots) && bots.length > 0 ? (
                  <>
                    <select
                      value={selectedBot || ''}
                      onChange={(e) => {
                        const botId = Number(e.target.value)
                        setSelectedBot(botId)
                        setFormData(prev => ({ ...prev, bot_session_id: botId }))
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      required
                    >
                      <option value="">-- Selecione um bot --</option>
                      {bots.map(bot => (
                        <option key={bot.id} value={bot.id}>
                          {bot.session_name || bot.name} {bot.isConnected ? '🟢' : '🔴'} {bot.phone_number ? `(${bot.phone_number})` : ''}
                        </option>
                      ))}
                    </select>

                    {/* Preview do Bot Selecionado */}
                    {selectedBot && (
                      <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        {(() => {
                          const selectedBotData = bots.find(b => b.id === selectedBot)
                          if (!selectedBotData) return null
                          
                          return (
                            <div className="flex items-center space-x-3">
                              <div className={`w-3 h-3 rounded-full ${
                                selectedBotData.isConnected ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h3 className="font-semibold text-gray-900">{selectedBotData.session_name || selectedBotData.name}</h3>
                                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    selectedBotData.isConnected 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {selectedBotData.isConnected ? 'Conectado' : 'Desconectado'}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {selectedBotData.phone_number ? `📱 ${selectedBotData.phone_number}` : '📱 Número não informado'}
                                </p>
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    )}

                    {/* Estatísticas Resumidas */}
                    <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="font-medium">{bots.length}</div>
                        <div className="text-xs text-gray-600">Total</div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="font-medium text-green-700">{bots.filter(b => b.isConnected).length}</div>
                        <div className="text-xs text-gray-600">Conectados</div>
                      </div>
                      <div className="text-center p-2 bg-red-50 rounded">
                        <div className="font-medium text-red-700">{bots.filter(b => !b.isConnected).length}</div>
                        <div className="text-xs text-gray-600">Desconectados</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <Bot className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium">Nenhum bot encontrado</p>
                    <p className="text-xs text-gray-400 mt-1">Crie um bot primeiro para associar menus</p>
                    <button
                      onClick={() => navigate('/bots/create')}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Criar Bot
                    </button>
                  </div>
                )}
              </div>

              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Menu Principal"
                  required
                />
              </div>

              {/* Conteúdo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conteúdo
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Conteúdo da mensagem que será enviada"
                />
              </div>

              {/* Tipo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'static' | 'dynamic' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="static">Estático</option>
                  <option value="dynamic">Dinâmico</option>
                </select>
              </div>

              {/* Menu Pai */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Menu Pai (Submenu)
                </label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    parent_id: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Menu Principal</option>
                  {menus.map(menu => (
                    <option key={menu.id} value={menu.id}>
                      {menu.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ordem de Exibição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ordem de Exibição
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              {/* Requer Atendimento Humano */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requires_human"
                  checked={formData.requires_human}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_human: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="requires_human" className="ml-2 block text-sm text-gray-900">
                  Requer atendimento humano
                </label>
              </div>

              {/* Tag automática na conversa */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="apply_auto_conversation_tag"
                    checked={applyAutoConversationTag}
                    onChange={(e) => {
                      const on = e.target.checked
                      setApplyAutoConversationTag(on)
                      if (!on) {
                        setFormData((prev) => ({ ...prev, auto_conversation_tag_id: null }))
                      }
                    }}
                    className="h-4 w-4 mt-0.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <label htmlFor="apply_auto_conversation_tag" className="block text-sm font-medium text-gray-900">
                      Aplicar tag na conversa ao escolher esta opção
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Quando o cliente selecionar este item do menu, a conversa recebe a tag escolhida (via catálogo de tags).
                      Desmarcado = nenhuma tag é aplicada automaticamente.
                    </p>
                  </div>
                </div>
                {applyAutoConversationTag && (
                  <div className="pl-6 border-l-2 border-indigo-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tag da conversa</label>
                    <select
                      value={formData.auto_conversation_tag_id ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          auto_conversation_tag_id: e.target.value ? parseInt(e.target.value, 10) : null
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione uma tag</option>
                      {contactTags.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    {contactTags.length === 0 && (
                      <p className="text-xs text-amber-600 mt-2">
                        Nenhuma tag cadastrada. Crie tags em Conversas ou Clientes antes de usar aqui.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Usar Template Personalizado */}
              <div className="border-t pt-6">
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="use_template"
                    checked={formData.use_template}
                    onChange={(e) => setFormData(prev => ({ ...prev, use_template: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="use_template" className="ml-2 block text-sm font-medium text-gray-900">
                    Usar template personalizado
                  </label>
                </div>

                {formData.use_template && (
                  <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                    {/* Seleção de Template */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template
                      </label>
                      <div className="flex space-x-2">
                        <select
                          value={formData.response_template_id || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            response_template_id: e.target.value ? parseInt(e.target.value) : undefined 
                          }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecione um template</option>
                          {templates.map(template => (
                            <option key={template.id} value={template.id}>
                              {template.name} ({template.template_type})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => navigate('/templates/management')}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                        >
                          Novo Template
                        </button>
                      </div>
                    </div>

                  </div>
                )}
              </div>

              {/* Botão de Criação */}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isCreating ? (
                    <Loading />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEditing ? 'Atualizar Menu' : 'Criar Menu'}
                    </>
                  )}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Lista de Menus Existentes */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Bot className="w-5 h-5 mr-2 text-green-500" />
                Menus Existentes
              </h2>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Bot selecionado:</span>
                    {selectedBot && Array.isArray(bots) ? (
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${
                          bots.find(b => b.id === selectedBot)?.isConnected ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <span className="font-medium text-gray-900">
                          {bots.find(b => b.id === selectedBot)?.session_name || bots.find(b => b.id === selectedBot)?.name}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          bots.find(b => b.id === selectedBot)?.isConnected 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {bots.find(b => b.id === selectedBot)?.isConnected ? 'Conectado' : 'Desconectado'}
                        </span>
                        {!showAllBots && (
                          <span className="text-xs text-gray-500">
                            ({menus.length} menu{menus.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">Nenhum bot selecionado</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={showAllBots}
                      onChange={(e) => {
                        setShowAllBots(e.target.checked)
                        if (e.target.checked) {
                          loadMenus()
                        } else if (selectedBot) {
                          loadMenus()
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span>Mostrar todos os bots</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {menus.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">Nenhum menu criado ainda</p>
                  <p className="text-sm mt-2">
                    {showAllBots 
                      ? 'Nenhum menu foi criado para nenhum bot ainda'
                      : selectedBot 
                        ? 'Nenhum menu foi criado para este bot ainda'
                        : 'Selecione um bot para ver seus menus'
                    }
                  </p>
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-gray-400">💡 Dicas:</p>
                    <ul className="text-xs text-gray-400 space-y-1">
                      <li>• Selecione um bot para criar menus específicos</li>
                      <li>• Use "Mostrar todos os bots" para ver todos os menus</li>
                      <li>• Menus podem ter submenus (hierarquia)</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {renderMenuTree(menus)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && menuToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirmar Exclusão
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Tem certeza que deseja deletar o menu <strong>"{menuToDelete.title}"</strong>?
                Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteMenu}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Deletar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuCreate